import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import CollectionProvider from 'providers/collection/CollectionProvider';
import SourceProvider from 'providers/source/SourceProvider';
import React, { useEffect, useState } from 'react';
import Layout from 'src/components/layout/Layout';
import { LayoutSection } from 'src/components/layout/Layout.types';
import useProfile from 'src/providers/hooks/useProfile';
import { PageMetadata } from 'types';
import { ArticleStatus } from 'types/enums';
import { CollectionData, Source } from 'types/models';
import { VLADIMIR_USER_ID } from 'utils/constants';
import { clearUrlParams, isSourceEmpty } from 'utils/global';
import SourceCard from './components/SourceCard';
import { RelatedPostsResponse } from 'api/api.types';

const EmptySourcePage = dynamic(() => import('./components/SourceEmptyPage'));
const PrivateSourcePage = dynamic(
  () => import('./components/SourcePrivatePage'),
);
const DraftProvider = dynamic(() => import('providers/source/DraftProvider'));
const SourceCardEditor = dynamic(
  () => import('./components/SourceCardEditor'),
  { ssr: false },
);

interface SourcePageProps {
  source: Source;
  collection?: CollectionData;
  similarSources?: Source[];
  /**
   * If true, after this article loads, if it belongs to the current user, a new idea will be added
   */
  startInEditMode?: boolean;
  pageMetadata: PageMetadata;
  sectionBefore?: LayoutSection;
  isInitialLoggedIn?: boolean;
  relatedPosts?: RelatedPostsResponse;
}

const SourcePage: React.FC<SourcePageProps> = ({
  source,
  collection,
  similarSources,
  startInEditMode,
  pageMetadata,
  sectionBefore,
  isInitialLoggedIn,
  relatedPosts,
}) => {
  const { profile } = useProfile();
  const isOwnArticle =
    profile?.id === source.userData.id || profile?.id === VLADIMIR_USER_ID;
  const isDraft = source.status === ArticleStatus.DRAFT;
  const [editMode, setEditMode] = useState(startInEditMode);
  const router = useRouter();

  useEffect(() => {
    if (router.query.edit) {
      if (isOwnArticle) {
        setEditMode(true);
      } else {
        clearUrlParams({ params: ['edit'], router, forceRefresh: true });
        setEditMode(false);
      }
    } else if (editMode) {
      setEditMode(false);
    }
  }, [router.query.edit]);

  if (isSourceEmpty(source) && !isDraft) {
    return <EmptySourcePage />;
  } else if (isDraft && !isOwnArticle) {
    return (
      <Layout
        pageMetadata={pageMetadata}
        sections={[{ content: <PrivateSourcePage /> }]}
      />
    );
  }

  if (isOwnArticle && editMode) {
    return (
      <DraftProvider draft={source}>
        <SourceCardEditor pageMetadata={pageMetadata} />
      </DraftProvider>
    );
  }

  return (
    <CollectionProvider
      collection={collection}
      isInitialRequestLoggedIn={isInitialLoggedIn}
    >
      <SourceProvider
        source={source}
        similarSources={similarSources}
        relatedPosts={relatedPosts}
      >
        <SourceCard pageMetadata={pageMetadata} sectionBefore={sectionBefore} />
      </SourceProvider>
    </CollectionProvider>
  );
};

export default SourcePage;
